import flatpickr from 'flatpickr';
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import { Options } from 'flatpickr/dist/types/options';
import isUndefined from 'lodash/isUndefined';
import { useEffect } from 'react';
import { RefObject } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useCallback } from 'react';
import { useUpdateEffect } from 'react-use';
import { useEffectOnce } from 'react-use';

interface FlatpickrParams {
    value: any;
    onChange: (value: Date | null) => void;
    options: Options;
    nodeRef: RefObject<HTMLInputElement>;
    parseDate: (inputValue: string) => Date | null;
    formatDate: (date: Date | null) => string;
}

const confirmIcon =
    '<svg width="16" height="18" fill="#6cbe45" xmlns="http://www.w3.org/2000/svg"><path d="M.571 7.796 0 8.14c.914 3.554 2.857 7.338 4.914 9.86l.572-.23C7.314 13.3 10.97 5.619 16 .574L15.657 0C11.314 3.44 7.2 8.37 4.457 12.955 3.314 11.924 1.6 9.401.571 7.796Z" fill="url(#paint0_linear_1671_2353)"/><defs><linearGradient id="paint0_linear_1671_2353" x1=".001" y1="9.004" x2="15.999" y2="9.004" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#AAD485"/><stop offset=".02" stop-color="#AAD485"/><stop offset="1" stop-color="#6CBE45"/></linearGradient></defs></svg>';

const isDateEqual = (dateA: Date | null, dateB: Date | null) =>
    Number(dateA) === Number(dateB);

export const useFlatpickr = ({
    value,
    onChange,
    options,
    nodeRef,
    parseDate,
    formatDate,
}: FlatpickrParams) => {
    const flatpickrRef = useRef<flatpickr.Instance | null>(null);
    const [date, setDate] = useState(value);
    const [inputValue, setInputValue] = useState<string>('');

    const composedOptions = useMemo(
        () => ({
            ...options,
            plugins: [
                confirmDatePlugin({
                    confirmIcon,
                    confirmText: '',
                    showAlways: options.enableTime,
                }),
            ],
            onChange: (selectedDates: Date[]) => {
                const selectedDate = selectedDates[0] || null;
                setDate(selectedDate);
            },
            parseDate: (value: string) => {
                return parseDate(value) || new Date('invalid');
            },
            formatDate: (value: Date) => {
                return formatDate(value);
            },
        }),
        [formatDate, options, parseDate]
    );

    /*
     * При ручном вводе значения в поле ввода
     */
    useEffect(() => {
        if (inputValue) {
            const inputDate = parseDate(inputValue);
            if (inputDate) {
                setDate(inputDate);
            }
        }
    }, [inputValue, parseDate]);

    useUpdateEffect(() => {
        if (!isDateEqual(value, date)) {
            onChange(date);
        }
    }, [date]);

    const createFlatpickrInstance = useCallback(() => {
        if (nodeRef.current) {
            const flatpickrInstance = flatpickr(
                nodeRef.current,
                composedOptions
            );
            flatpickrRef.current = flatpickrInstance;
            if (!isUndefined(value)) {
                flatpickrInstance.setDate(value, false);
            }
        }
    }, [composedOptions, nodeRef, value]);

    const destroyFlatpickrInstance = useCallback(() => {
        const flatpickrInstance = flatpickrRef.current;
        if (flatpickrInstance) {
            flatpickrInstance.destroy();
            flatpickrRef.current = null;
        }
    }, []);

    useUpdateEffect(() => {
        const flatpickrInstance = flatpickrRef.current;
        if (flatpickrInstance) {
            flatpickrInstance.setDate(value, false);
        }
    }, [value]);

    useEffectOnce(() => {
        const handleBlur = (event: Event) => {
            event.stopImmediatePropagation();
            // flatpickrRef.current?.close();
        };
        const handleKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.stopImmediatePropagation();
            }
        };
        const handleKeyup = (event: KeyboardEvent) => {
            const eventTarget = event.target as HTMLInputElement;
            setInputValue(eventTarget?.value);
        };
        nodeRef.current?.addEventListener('blur', handleBlur);
        nodeRef.current?.addEventListener('keydown', handleKeydown);
        nodeRef.current?.addEventListener('keyup', handleKeyup);
        return () => {
            nodeRef.current?.removeEventListener('blur', handleBlur);
            nodeRef.current?.removeEventListener('keydown', handleKeydown);
            nodeRef.current?.removeEventListener('keyup', handleKeyup);
        };
    });

    useEffectOnce(() => {
        createFlatpickrInstance();
        return () => {
            destroyFlatpickrInstance();
        };
    });
};
